/* src/components/OnboardingChecklist.jsx
 * Stateful onboarding checklist — replaces the static Quick Start block.
 * Reads real Firestore form data + Google Ads connection status to show
 * exactly one "Next →" action at all times.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { googleAdsApi, smsApi, usageApi } from '../utils/api';
import {
  CHECKLIST_TEXTING_LABEL,
  CHECKLIST_TEXTING_SUB,
  isSmsTextingChecklistDone,
} from '../utils/smsOnboardingUx';
import { OnboardingChecklistSkeleton } from './Skeleton';

/** True when this workspace has accepted at least one lead (not the owner's pooled count). */
export function isGoLiveDone(usage) {
  return (usage?.workspaceUsed || 0) > 0;
}

export default function OnboardingChecklist({ userProfile }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();

  const [forms, setForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(true);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(true);
  const [smsTextingDone, setSmsTextingDone] = useState(false);
  const [loadingSms, setLoadingSms] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [usage, setUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(true);

  // Firestore real-time listener for this workspace's forms
  useEffect(() => {
    if (!currentUser || !selectedWorkspaceId) {
      setLoadingForms(false);
      return;
    }

    setLoadingForms(true);
    const q = query(
      collection(db, 'forms'),
      where('workspaceId', '==', selectedWorkspaceId)
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setForms(list);
        setLoadingForms(false);
      },
      (err) => {
        console.error('[OnboardingChecklist] Firestore error:', err);
        setFetchError(true);
        setLoadingForms(false);
      }
    );

    return () => unsub();
  }, [currentUser, selectedWorkspaceId]);

  // One-shot Google Ads connection check
  useEffect(() => {
    if (!currentUser || !selectedWorkspaceId) {
      setLoadingGoogle(false);
      return;
    }

    setLoadingGoogle(true);
    googleAdsApi
      .status(selectedWorkspaceId)
      .then((res) => {
        setGoogleConnected(!!res?.connected);
      })
      .catch(() => {
        // Non-fatal — just treat as not connected
        setGoogleConnected(false);
      })
      .finally(() => setLoadingGoogle(false));
  }, [currentUser, selectedWorkspaceId]);

  // One-shot SMS status — done only when active + approved (not past_due)
  useEffect(() => {
    if (!currentUser || !selectedWorkspaceId) {
      setLoadingSms(false);
      return;
    }

    setLoadingSms(true);
    smsApi
      .getSmsStatus(selectedWorkspaceId)
      .then((res) => {
        setSmsTextingDone(isSmsTextingChecklistDone(res?.smsNumber));
      })
      .catch(() => {
        setSmsTextingDone(false);
      })
      .finally(() => setLoadingSms(false));
  }, [currentUser, selectedWorkspaceId]);

  useEffect(() => {
    if (!currentUser || !selectedWorkspaceId) {
      setLoadingUsage(false);
      setUsage(null);
      return undefined;
    }

    setLoadingUsage(true);
    let cancelled = false;
    usageApi
      .getUsage(selectedWorkspaceId)
      .then((data) => {
        if (!cancelled && data?.success) setUsage(data);
      })
      .catch(() => {
        if (!cancelled) setUsage(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingUsage(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser, selectedWorkspaceId]);

  // ---------------------------------------------------------------------------
  // Derive checklist state
  // ---------------------------------------------------------------------------

  const firstFormId = forms.length > 0 ? forms[0].id : null;
  const editBase = firstFormId ? `/forms/${firstFormId}/edit` : '/forms/new';

  // Row 2: settings.businessContext (stored at form.settings.businessContext)
  const hasKnowledge = forms.some(
    (f) => f.settings?.businessContext && f.settings.businessContext.trim() !== ''
  );

  // Row 3: read persisted trackingVerification where available; fall back to the
  // trackingPixels proxy for forms that have never been swept.
  const GREEN_TV_STATUSES = new Set(['verified', 'offline_armed']);
  const DRIFT_TV_STATUSES = new Set(['needs_connection', 'error']);

  // Forms whose sweep result shows at least one healthy platform.
  const verifiedForms = forms.filter((f) => {
    const tv = f.trackingVerification;
    if (!tv) return false;
    return Object.entries(tv)
      .filter(([k]) => k !== '_lastSwept')
      .some(([, v]) => GREEN_TV_STATUSES.has(v?.status));
  });

  // Forms with a swept-but-broken platform (the drift signal).
  const driftForms = forms.filter((f) => {
    const tv = f.trackingVerification;
    if (!tv) return false;
    return Object.entries(tv)
      .filter(([k]) => k !== '_lastSwept')
      .some(([, v]) => DRIFT_TV_STATUSES.has(v?.status));
  });

  // Most recent _lastSwept across all forms — used for "last checked" date.
  const lastSweptTs = forms.reduce((best, f) => {
    const ts = f.trackingVerification?._lastSwept?.toDate?.() ?? null;
    return ts && (!best || ts > best) ? ts : best;
  }, null);

  const hasVerifiedByPersisted = verifiedForms.length > 0;
  const hasDrift = driftForms.length > 0;

  // "Done" requires the OFFLINE path actually armed — a green persisted status
  // (verified / offline_armed) written by the weekly sweep OR a passing manual
  // "Test Now". A client-side pixel with no offline reporting is NOT done.
  const PIXEL_ONLY_TV_STATUSES = new Set(['pixel_only']);

  // Forms whose tracking is set but only client-side armed (pixel fires, offline
  // closed-deal reporting not connected) — the moat is silently off.
  const pixelOnlyForms = forms.filter((f) => {
    const tv = f.trackingVerification;
    if (!tv) return false;
    return Object.entries(tv)
      .filter(([k]) => k !== '_lastSwept')
      .some(([, v]) => PIXEL_ONLY_TV_STATUSES.has(v?.status));
  });
  const hasPixelOnly = pixelOnlyForms.length > 0;

  // Forms that have a pixel configured but no verification result yet (never tested
  // and never swept) — genuinely "checking", not a false green and not a failure.
  const hasUntestedPixel = forms.some(
    (f) =>
      Array.isArray(f.trackingPixels) &&
      f.trackingPixels.length > 0 &&
      (() => {
        const tv = f.trackingVerification;
        if (!tv) return true;
        const platformEntries = Object.entries(tv).filter(([k]) => k !== '_lastSwept');
        return platformEntries.length === 0;
      })()
  );

  const hasTracking = hasVerifiedByPersisted;

  // Row 4: owner-fallback guarantees delivery the moment a form exists.
  // Explicit recipients are surfaced in sub-text only.
  const hasForms = forms.length > 0;
  const explicitRecipients = forms.flatMap(
    (f) => (Array.isArray(f.settings?.recipients) ? f.settings.recipients : [])
  );
  const ownerEmail = currentUser?.email || userProfile?.email || '';

  const isLive = isGoLiveDone(usage);

  const items = [
    {
      id: 'form',
      label: 'Create your first form',
      done: hasForms,
      cta: '/onboarding',
      subText: null,
    },
    {
      id: 'knowledge',
      label: 'Add your business knowledge',
      done: hasKnowledge,
      cta: `${editBase}?tab=spam-settings`,
      subText: null,
    },
    {
      id: 'tracking',
      label: 'Connect & verify tracking',
      done: hasTracking && !hasDrift,
      // Amber warning for drift (was working, now broken) OR pixel-only (client pixel
      // on but offline closed-deal reporting never armed — the moat silently off).
      driftWarning: hasDrift || (hasPixelOnly && !hasVerifiedByPersisted),
      cta: `${editBase}?tab=tracking`,
      isEmphasis: true,
      nudge: "This is your edge: verified conversion tracking is what competitors can't match.",
      subText: hasDrift
        ? `Needs attention: ${driftForms[0].name || 'a form'} tracking stopped verifying`
        : hasVerifiedByPersisted
        ? lastSweptTs
          ? `Verified ✓ · last checked ${lastSweptTs.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
          : 'Verified ✓'
        : hasPixelOnly
        ? "Client pixel on · offline conversions not armed, closed deals won't report"
        : hasUntestedPixel
        ? 'Checking… run Test to verify closed-deal reporting'
        : googleConnected
        ? 'Google connected, add a pixel to complete'
        : null,
    },
    {
      id: 'recipients',
      label: 'Set who gets notified',
      // Owner-fallback always delivers — done as soon as a form exists
      done: hasForms,
      cta: `${editBase}?tab=spam-settings`,
      subText: hasForms
        ? explicitRecipients.length > 0
          ? `Notifying: ${explicitRecipients.join(', ')}`
          : `Notifications go to you (${ownerEmail}) · add teammates to share leads`
        : null,
    },
    {
      id: 'live',
      label: 'Go live, first submission in',
      done: isLive,
      cta: '/form-builder',
      subText: null,
    },
    {
      id: 'texting',
      label: CHECKLIST_TEXTING_LABEL,
      done: smsTextingDone,
      cta: '/text-messaging',
      subText: CHECKLIST_TEXTING_SUB,
    },
  ];

  const doneCount = items.filter((i) => i.done).length;
  const firstIncomplete = items.find((i) => !i.done);

  const isLoading = loadingForms || loadingGoogle || loadingSms || loadingUsage;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div
      style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '8px',
        marginBottom: '2rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.25rem',
        }}
      >
        <h3 style={{ margin: 0, color: '#333' }}>🚀 Setup checklist</h3>
        {!isLoading && !fetchError && (
          <span
            style={{
              fontSize: '0.85rem',
              fontWeight: 600,
              color: doneCount === 6 ? '#28a745' : '#666',
              background: doneCount === 6 ? '#d1fae5' : '#f3f4f6',
              padding: '0.2rem 0.6rem',
              borderRadius: '999px',
            }}
          >
            {doneCount} of 6 done
          </span>
        )}
      </div>

      {fetchError ? (
        <p style={{ color: '#dc3545', margin: 0, fontSize: '0.9rem' }}>
          Couldn't load setup status. Refresh to retry.
        </p>
      ) : isLoading ? (
        <OnboardingChecklistSkeleton rows={6} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          {/* Wizard CTA banner — only shown when no forms exist yet */}
          {!hasForms && (
            <div style={{
              background: 'linear-gradient(135deg, #eff6ff 0%, #f0f4ff 100%)',
              border: '1px solid #c7d2fe',
              borderRadius: '8px',
              padding: '1rem 1.25rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}>
              <div>
                <div style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.92rem', marginBottom: '0.2rem' }}>
                  New to gocta.ai?
                </div>
                <div style={{ fontSize: '0.82rem', color: '#4b5563' }}>
                  The setup wizard takes &lt;5 minutes: URL to live form with tracking verified.
                </div>
              </div>
              <button
                onClick={() => navigate('/onboarding')}
                style={{
                  flexShrink: 0,
                  padding: '0.45rem 1rem',
                  background: '#1e40af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                Use wizard →
              </button>
            </div>
          )}
          {items.map((item, idx) => {
            const isNext = firstIncomplete?.id === item.id;
            const rowStyle = {
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem',
              padding: '0.65rem 0.75rem',
              borderRadius: '6px',
              borderLeft: item.isEmphasis ? '3px solid #f59e0b' : '3px solid transparent',
              background: isNext ? '#f0f4ff' : 'transparent',
              marginBottom: idx < items.length - 1 ? '0.25rem' : 0,
            };

            return (
              <div key={item.id} style={rowStyle}>
                {/* Status icon */}
                <span
                  style={{
                    fontSize: '1rem',
                    color: item.driftWarning ? '#d97706' : item.done ? '#28a745' : '#bbb',
                    flexShrink: 0,
                    marginTop: '0.1rem',
                    fontWeight: 700,
                  }}
                >
                  {item.driftWarning ? '⚠' : item.done ? '✓' : '○'}
                </span>

                {/* Label + sub-text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      fontSize: '0.95rem',
                      color: item.done ? '#555' : '#333',
                      fontWeight: isNext ? 600 : 400,
                      textDecoration: item.done ? 'line-through' : 'none',
                    }}
                  >
                    {item.label}
                  </span>

                  {item.subText && (
                    <div style={{ fontSize: '0.8rem', color: item.driftWarning ? '#b45309' : '#888', marginTop: '0.1rem' }}>
                      {item.subText}
                    </div>
                  )}

                  {item.isEmphasis && (
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: '#92400e',
                        fontStyle: 'italic',
                        marginTop: '0.2rem',
                      }}
                    >
                      {item.nudge}
                    </div>
                  )}
                </div>

                {/* Next button — only on first incomplete */}
                {isNext && (
                  <button
                    onClick={() => navigate(item.cta)}
                    style={{
                      flexShrink: 0,
                      padding: '0.3rem 0.85rem',
                      background: '#1e40af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Next →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
